
import Deferred from '~/deferred';
import BlobStorage from './blob-storage';
import ImageMedia from './ImageMedia.svelte';
import VideoMedia from './VideoMedia.svelte';

const MEDIA = {
  IMAGE: ImageMedia,
  VIDEO: VideoMedia
};

// Share blobs between playlists
const blobs = new BlobStorage();

const timeout = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export default class Playlist {

  /**
   * 
   * @param {MediaItem[]} input 
   * @param {boolean?} protect 
   */
  constructor (input, protect = false) {
    this.playing = false;
    this.input = input;
    this.load_counter = input.length;
    /** @type {MediaEntry[]} */
    this.list = [];
    /** @type {string[]} */
    this.existing_urls = [];
    this.pointer = -1;
    this.pointer_prev;
    this.paused = false;
    this._protected = protect;
    this._canPlay = false;
    this._canPlay_lock = new Deferred();
    this._fullyLoaded_lock = new Deferred();
    /** @type {MediaEventHandler<keyof MediaEvent>[]} */
    this.loadListeners = [];
    this._loadPlaylist();
  }

  _loadPlaylist () {
    this.list = this.input.map(this._transformMediaItem.bind(this));

    Promise.all(this.list.map(this._loadResource.bind(this)))
      .then(() => {
      //   console.log('existing_urls', this.existing_urls);
        blobs.update(this.existing_urls); // Should this be done only when playlist is started
        this._fullyLoaded_lock.resolve(true);
      });
  }

  /**
   * Transform MediaItem to MediaEntry with additional metadata for playlist management
   * @param {MediaItem} item 
   * @param {number} index 
   * @returns {MediaEntry}
   */
  _transformMediaItem (item, index) {
    return {
      item: Object.assign({ url_blob: '' }, item),
      id: `${index}--${item.id}`,   // protect against dupes
      index,
      load_count: 0,
      play_count: 0,
      loaded: false,
      component: MEDIA[item.mediaType]
    };
  }

  /**
   * 
   * @param {MediaEntry} media 
   */
  async _loadResource (media) {

    try {
      const url = media.item.mediaUrl;
      const url_blob = await blobs.getObjectURL(url, this._protected);
      media.load_count++;

      if (url_blob) {
        this.existing_urls.push(url);
        media.item.url_blob = url_blob;

        media.loaded = true;
        this.load_counter--;

        if (!this._canPlay) {
          this._canPlay = true;
          if (this.load_counter) {
            // Wait a bit so other playlist resources can finish as well
            await timeout(10);
          }
          this._canPlay_lock.resolve(true);
        }
        this._signal('media.loaded', media);
      }
      else if (media.load_count < 3) {
        // Wait and try again
        await timeout(500);
        this._loadResource(media);
      }
      else {
        this.load_counter--;
      }
    } catch (e) {
      console.warn(e);
    }
  }

  waitCanPlay () {
    return this._canPlay_lock.promise;
  }

  waitFullyLoaded () {
    return this._fullyLoaded_lock.promise;
  }

  /**
   * 
   * @returns {MediaEntry}
   */
  nextReadyItem () {
    const index = this.pointer + 1 < this.list.length ? this.pointer + 1 : 0;
    // Reorder list so that index item is the first
    const ordered = [...this.list.slice(index), ...this.list.slice(0, index)];
    const next_ready = ordered.find(x => x.loaded && !x.error);
    return next_ready;
  }

  item (pointer = this.pointer) {
    return pointer < 0 ? null : this.list[pointer];
  }

  /**
   * Returns the current view of the playlist, which may include the previous item for crossfading purposes.
   * @returns {MediaView | [MediaEntry]}
   */
  currentView () {
    const prev = this.item(this.pointer_prev);
    const current = this.item();
    return prev && prev != current ? [prev, current] : [current];
  }

  /**
   * Returns the next view of the playlist, which may include the current item for crossfading purposes.
   * @returns {MediaView | [MediaEntry]}
   */
  proceedNextItem () {
    this.paused = false;
    const current = this.item();
    const next = this.nextReadyItem();

    if (current) {
      current.playing = false;
    }
    if (next) {
      next.playing = true;
      this.pointer_prev = this.pointer;
      this.pointer = next.index;
      // New ID for each play so that we get crossfades on playlists < 3
      next.id = `${next.index}_${next.item.id}_${++next.play_count}`;
    }
    else {
      throw new Error(`No playable items left ${this.toString()}`);
    }

    return current && current != next ? [current, next] : [next];
  }

  /**
   * Resumes playback of the current item or proceeds to the next item if none is playing.
   * @returns {MediaView | [MediaEntry]}
   */
  resume () {
    this.paused = false;
    const current = this.item();

    if (current) {
      const prev = this.item(this.pointer_prev);
      current.playing = true;
      return prev && prev != current ? [prev, current] : [current];
    }
    else {
      return this.proceedNextItem();
    }
  }

  /**
   * Stop playback and return the current view of the playlist, which may include the previous item for crossfading purposes.
   * @returns {MediaEntry[]}
   */
  stop () {
    const current = this.item();
    if (current) {
      current.playing = false;
      this.paused = true;
      return this.currentView();
    }
    return [];
  }

  /**
   * Returns true if the playlist should update to new input, false if it can be reused as is.
   * @param {MediaItem[]} input 
   * @returns {boolean}
   */
  shouldUpdate (input) {
    const ids_current = Playlist.identity(this.input);
    const ids_input = Playlist.identity(input);

    return ids_current != ids_input;
  }

  toString () {
    const content = Playlist.identity(this.input);
    return `Playlist(${this.input.length}) [${content}]`;
  }

  /**
   * @param {any} event 
   * @param {any} data 
   */
  _signal (event, data) {
    this.loadListeners.forEach(fn => fn({ event, data }));
  }

  /**
   * Add a listener for playlist events such as 'media.loaded'.
   * @param {MediaEventHandler<keyof MediaEvent>} callback 
   */
  addLoadListener (callback) {
    this.loadListeners.push(callback);
  }

  static identity (input = []) {
    return input.map(x => x.id).join(' -> ');
  }

}
